import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

const COLLECTION = 'profiles';

export const getUserProfile = async (uid) => {
  if (!uid) throw new Error('Missing uid for getUserProfile');
  const ref = doc(db, COLLECTION, uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

export const findProfileByEmail = async (email) => {
  if (!email) return null;
  try {
    const col = collection(db, COLLECTION);
    const q = query(col, where('email', '==', email.toLowerCase().trim()), limit(1));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const firstDoc = snap.docs[0];
    return { id: firstDoc.id, ...firstDoc.data() };
  } catch (e) {
    console.error('Error finding profile by email:', e);
    return null;
  }
};

export const upsertUserProfile = async (uid, data) => {
  if (!uid) throw new Error('Missing uid for upsertUserProfile');
  const ref = doc(db, COLLECTION, uid);
  
  // Check if profile exists
  let existingProfile = null;
  try {
    const snap = await getDoc(ref);
    if (snap.exists()) {
      existingProfile = snap.data();
    }
  } catch (e) {
    // Profile doesn't exist yet
  }
  
// diagnostic logging removed

  // If profile exists, only update fields that are explicitly provided
  // If profile doesn't exist, create with defaults
  // Only persist the minimal allowed profile fields to the profiles collection.
  // Allowed fields: firstName, lastName, gender, yob, email, phone, userStatus, userType, vendorId
  const updateData = { updatedAt: serverTimestamp() };

  if (existingProfile) {
    if (data.firstName !== undefined) updateData.firstName = data.firstName;
    if (data.lastName !== undefined) updateData.lastName = data.lastName;
    if (data.gender !== undefined) updateData.gender = data.gender;
    if (data.yob !== undefined) updateData.yob = data.yob;
    if (data.phoneNumber !== undefined) updateData.phoneNumber = data.phoneNumber;
    if (data.userAreaCode !== undefined) updateData.userAreaCode = data.userAreaCode;
    if (data.email !== undefined) updateData.email = data.email;
    if (data.userType !== undefined) updateData.userType = data.userType || existingProfile.userType || 'Vendor';
    if (data.userStatus !== undefined) updateData.userStatus = data.userStatus || existingProfile.userStatus || 'Active';
    if (data.vendorId !== undefined) updateData.vendorId = data.vendorId;
    if (data.vendorRole !== undefined) updateData.vendorRole = data.vendorRole;
    if (data.linkApproval !== undefined) updateData.linkApproval = data.linkApproval;
    if (data.acknowledgedPolicies !== undefined) updateData.acknowledgedPolicies = !!data.acknowledgedPolicies;
    if (data.updatedBy !== undefined) updateData.updatedBy = data.updatedBy;
    if (data.updatedByName !== undefined) updateData.updatedByName = data.updatedByName;
  } else {
    // New profile - initialize only allowed fields
    updateData.firstName = data.firstName || '';
    updateData.lastName = data.lastName || '';
    updateData.gender = data.gender || '';
    updateData.yob = data.yob || '';
    updateData.phoneNumber = data.phoneNumber || '';
    // Do not persist business phone on the profile document. Business
    // contact information is canonical on the `vendors` document.
    updateData.userAreaCode = data.userAreaCode || '';
    updateData.email = data.email || '';
    updateData.userType = data.userType || 'Vendor';
    updateData.userStatus = data.userStatus || 'Active';
    updateData.vendorId = data.vendorId || '';
    if (data.vendorRole !== undefined) updateData.vendorRole = data.vendorRole || '';
    if (data.linkApproval !== undefined) updateData.linkApproval = data.linkApproval || '';
    updateData.acknowledgedPolicies = !!data.acknowledgedPolicies;
    if (data.updatedBy !== undefined) updateData.updatedBy = data.updatedBy;
    if (data.updatedByName !== undefined) updateData.updatedByName = data.updatedByName;
    updateData.createdAt = serverTimestamp();
  }

  // Use setDoc with merge to preserve any other unrelated fields (but we avoid writing business fields here)
    try {
      await setDoc(ref, updateData, { merge: true });
    } catch (e) {
      console.error('profilesService.upsertUserProfile setDoc failed', e);
      return { __error: { code: e.code || '', message: `[profilesService.upsertUserProfile] ${e.message || e}` } };
    }

  // Best effort read-back; if offline, return what we wrote
  try {
    const updated = await getDoc(ref);
    if (updated.exists()) return { id: updated.id, ...updated.data() };
  } catch (e) {
    console.error('profilesService.upsertUserProfile getDoc failed', e);
    return { __error: { code: e.code || '', message: `[profilesService.upsertUserProfile:getDoc] ${e.message || e}` } };
  }
  return { id: uid, ...updateData };
};

export const listUserProfiles = async () => {
  const col = collection(db, COLLECTION);
  const snap = await getDocs(col);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const listProfilesByType = async (userType) => {
  if (!userType) return [];
  try {
    const col = collection(db, COLLECTION);
    const q = query(col, where('userType', '==', userType));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    console.error('profilesService.listProfilesByType failed', e);
    return [];
  }
};


// Link or approve a user to a vendor via the server-side onRequest endpoint.
// payload: { vendorId, profileId, action }

export const linkUserToVendor = async (vendorId, profileId, action = 'link') => {
  if (!vendorId || !profileId) throw new Error('vendorId and profileId are required');

  let _HOST = ''
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_FIREBASE_PROJECT_URL) {
      _HOST = import.meta.env.VITE_FIREBASE_PROJECT_URL;
    } else if (typeof process !== 'undefined' && process.env && process.env.FIREBASE_PROJECT_URL) {
      _HOST = process.env.FIREBASE_PROJECT_URL;
    }
  } catch (e) {
    // import.meta may not be available in some runtimes; fall back to process.env or default
    if (typeof process !== 'undefined' && process.env && process.env.FIREBASE_PROJECT_URL) {
      _HOST = process.env.FIREBASE_PROJECT_URL;
    }
  }

  const FUNCTION_NAME = 'linkusertovendor';
  const url = `https://${FUNCTION_NAME}-${_HOST}`;
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vendorId, profileId, action })
    });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => null);
      throw new Error(`HTTP ${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`);
    }
    // Try parse JSON, fallback to text
      try {
        const data = await resp.json();
        return data;
      } catch (e) {
      return await resp.text().catch(() => null);
    }
  } catch (e) {
    console.error('profilesService.linkUserToVendor failed', e);
    throw e;
  }
};

// Attempt to load profiles for a vendor via the deployed onRequest endpoint first,
// falling back to the client-side Firestore query when network/CORS or auth
// issues prevent the remote call from succeeding. Returns an object with
// `{ profiles: Array, loadError?: string }` so callers can surface fallback
// diagnostics to users.
export const listProfilesByVendorRemote = async (vendorId) => {
  if (!vendorId) return { profiles: [] };

  let _HOST = ''
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_FIREBASE_PROJECT_URL) {
      _HOST = import.meta.env.VITE_FIREBASE_PROJECT_URL;
    } else if (typeof process !== 'undefined' && process.env && process.env.FIREBASE_PROJECT_URL) {
      _HOST = process.env.FIREBASE_PROJECT_URL;
    }
  } catch (e) {
    // import.meta may not be available in some runtimes; fall back to process.env or default
    if (typeof process !== 'undefined' && process.env && process.env.FIREBASE_PROJECT_URL) {
      _HOST = process.env.FIREBASE_PROJECT_URL;
    }
  }

  const FUNCTION_NAME = 'listprofilesbyvendor';
  const baseUrl = `https://${FUNCTION_NAME}-${_HOST}`;
  const url = `${baseUrl}?vendorId=${encodeURIComponent(vendorId)}`;
  try {
    const resp = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
    if (!resp.ok) {
      const txt = await resp.text().catch(() => null);
      throw new Error(`HTTP ${resp.status} ${resp.statusText}${txt ? `: ${txt}` : ''}`);
    }

    let payload = null;
    try {
      payload = await resp.json();
    } catch (jsonErr) {
      const txt = await resp.text().catch(() => null);
      if (txt) {
        try { payload = JSON.parse(txt); } catch (pErr) { payload = txt; }
      }
    }

    let linked = [];
    if (Array.isArray(payload)) {
      linked = payload;
    } else if (payload && payload.profiles) {
      linked = payload.profiles;
    } else if (payload && payload.data && payload.data.profiles) {
      linked = payload.data.profiles;
    } else {
      linked = [];
    }

    return { profiles: linked || [] };
  } catch (e) {
    const msg = String(e?.message || e || '');
    // Unknown error — rethrow for caller to handle
    throw e;
  }
};
